import React, { useEffect, useState, Fragment } from "react";
import { useDispatch } from "react-redux";
import { Row, Col, Card, Button, Table } from "react-bootstrap";
import { Fn_FillListData, Fn_GetReport } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import PageTitle from "../layouts/PageTitle";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const YEAR_LIST = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

const EsiDeductionDetail = () => {
  const dispatch = useDispatch();

  const readSession = () => ({
    firm: (() => {
      try {
        return JSON.parse(localStorage.getItem("selectedFirm") || "null");
      } catch {
        return null;
      }
    })(),
    month: localStorage.getItem("selectedPayrollMonth") || "",
    year: parseInt(localStorage.getItem("selectedPayrollYear") || "0") || currentYear,
  });

  const [sessionFirm, setSessionFirm] = useState(() => readSession().firm);
  const [selectedMonth, setSelectedMonth] = useState(() => readSession().month);
  const [selectedYear, setSelectedYear] = useState(() => readSession().year);

  // States
  const [firmList, setFirmList] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Sync session changes from storage and sessionChange events
  useEffect(() => {
    const onStorage = () => {
      const s = readSession();
      setSessionFirm(s.firm);
      setSelectedMonth(s.month);
      setSelectedYear(s.year);
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("sessionChange", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("sessionChange", onStorage);
    };
  }, []);

  // Fetch initial masters
  useEffect(() => {
    const firmApiUrl = `${API_WEB_URLS.MASTER}/0/token/${API_WEB_URLS.FirmMaster}/Id/0`;
    Fn_FillListData(dispatch, setFirmList, "gridData", firmApiUrl);
  }, [dispatch]);

  // Load report data when inputs change
  useEffect(() => {
    const firmId = sessionFirm?.Id || 0;
    if (firmId && selectedMonth && selectedYear) {
      loadReportData(firmId, selectedMonth, selectedYear, searchTerm);
    } else {
      setReportData(null);
    }
  }, [sessionFirm, selectedMonth, selectedYear, searchTerm]);

  const loadReportData = async (firmId, monthName, year, searchQuery) => {
    setIsLoading(true);
    try {
      const fd = new FormData();
      fd.append("F_FirmMaster", firmId);
      fd.append("PayrollMonth", monthName);
      fd.append("PayrollYear", year);
      fd.append("EmployeeSearch", searchQuery || "");

      const records = await Fn_GetReport(
        dispatch,
        () => {},
        "data",
        "GetEsiDeductionDetail/0/token",
        { arguList: { id: 0, formData: fd } },
        true
      );

      let parsedData = null;
      if (records && records.length > 0) {
        const firstKey = Object.keys(records[0])[0];
        if (firstKey !== undefined && firstKey !== null) {
          const jsonString = records.map(row => row[firstKey] || "").join("");
          if (jsonString) {
            const rawObj = JSON.parse(jsonString);
            parsedData = rawObj.data || rawObj;
          }
        }
      }
      setReportData(parsedData);
    } catch (err) {
      console.error("Error loading report data:", err);
      setReportData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getRecords = () => {
    if (!reportData || !reportData.Records) return [];
    return reportData.Records.map((rec) => ({
      sno: rec.Sno || "",
      esiNo: rec.ESINo || "",
      name: (rec.EmployeeName || rec.Name || "").toUpperCase(),
      fatherName: (rec.FatherName || "").toUpperCase(),
      eligibleSalary: parseFloat(rec.EligibleSalary || 0),
      employeeESI: parseFloat(rec.EmployeeESI || 0),
      employerESI: parseFloat(rec.EmployerESI || 0)
    }));
  };

  const records = getRecords();

  // jsPDF export matching rptESIDeductionDetail.pdf layout
  const exportToPDF = () => {
    if (records.length === 0) {
      alert("No data available.");
      return;
    }

    const pdf = new jsPDF("p", "mm", "a4");
    
    // Top Title Block
    pdf.setFontSize(11);
    pdf.setFont("Helvetica", "bold");
    pdf.text(sessionFirm?.Name?.toUpperCase() || "", 105, 12, { align: "center" });

    pdf.setFontSize(8.5);
    pdf.text(`EMPLOYEES STATE INSURANCE REPORT FOR THE MONTH OF ${(selectedMonth || "").toUpperCase()} ${selectedYear}`, 105, 17, { align: "center" });

    pdf.setFont("Helvetica", "bold");
    pdf.text(`CODE NO. : ${getFirmEsiNo()}`, 14, 23);

    const body = records.map(row => [
      row.sno,
      row.esiNo,
      `${row.name}\n${row.fatherName}`,
      row.eligibleSalary.toFixed(2),
      row.employeeESI.toFixed(2),
      row.employerESI.toFixed(2)
    ]);

    // Calculate totals
    const totalWages = records.reduce((sum, r) => sum + r.eligibleSalary, 0);
    const totalEmployeeESI = records.reduce((sum, r) => sum + r.employeeESI, 0);
    const totalEmployerESI = records.reduce((sum, r) => sum + r.employerESI, 0);

    body.push([
      { content: "Total", colSpan: 3, styles: { fontStyle: "bold", halign: "right" } },
      { content: totalWages.toFixed(2), styles: { fontStyle: "bold" } },
      { content: totalEmployeeESI.toFixed(2), styles: { fontStyle: "bold" } },
      { content: totalEmployerESI.toFixed(2), styles: { fontStyle: "bold" } }
    ]);

    autoTable(pdf, {
      head: [[
        "Sr. No.",
        "AC No",
        "EMPLOYEE NAME\nFATHER NAME",
        "ELIGIBLE SALARY",
        "EMPLOYEE ESI",
        "EMPLOYER ESI"
      ]],
      body,
      startY: 27,
      margin: { left: 10, right: 10 },
      styles: { fontSize: 7.5, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.05 },
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: "bold" }
    });

    pdf.save(`ESI_Deduction_Detail_${sessionFirm?.Name}_${selectedMonth}_${selectedYear}.pdf`);
  };

  const getFirmEsiNo = () => {
    if (reportData?.FirmDetails?.ESINo) return reportData.FirmDetails.ESINo;
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.ESINo || "N/A";
      }
    } catch (e) {}
    return "N/A";
  };

  const printStyles = `
    @media print {
      body * {
        visibility: hidden !important;
      }
      #print-esi-deduction-container, #print-esi-deduction-container * {
        visibility: visible !important;
      }
      #print-esi-deduction-container {
        display: block !important;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        background: #fff;
        color: #000;
        font-family: Arial, sans-serif;
      }
      .no-print {
        display: none !important;
      }
      .esi-deduction-print-table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 15px;
      }
      .esi-deduction-print-table th, .esi-deduction-print-table td {
        border: 1px solid #000;
        padding: 5px;
        font-size: 8px;
        text-align: left;
        vertical-align: top;
      }
      .esi-deduction-print-table th {
        background: #f2f2f2;
        font-weight: bold;
      }
    }
  `;

  return (
    <Fragment>
      <style>{printStyles}</style>

      <div className="no-print">
        <PageTitle activeMenu="ESI Deduction Detail" motherMenu="Reports" pageContent="ESI Contribution Report" />
      </div>

      {/* Filters Card */}
      <Card className="no-print mb-4 shadow-sm border-0" style={{ borderRadius: "10px", background: "#ffffff" }}>
        <Card.Body className="p-4">
          <Row className="align-items-end g-3">
            {/* Firm */}
            <Col lg={4} md={6}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>
                Firm <span className="badge bg-light text-success ms-1 border" style={{ fontSize: "11px" }}>🔒 Header Locked</span>
              </label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px", backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                value={sessionFirm?.Name || "Please select Firm from Header"}
                readOnly
                disabled
              />
            </Col>

            {/* Month */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Month</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  if (e.target.value) localStorage.setItem("selectedPayrollMonth", e.target.value);
                  else localStorage.removeItem("selectedPayrollMonth");
                }}
              >
                <option value="">-- Month --</option>
                {MONTH_NAMES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </Col>

            {/* Year */}
            <Col lg={2} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Year</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(parseInt(e.target.value) || 0);
                  if (e.target.value) localStorage.setItem("selectedPayrollYear", e.target.value);
                  else localStorage.removeItem("selectedPayrollYear");
                }}
              >
                {YEAR_LIST.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </Col>

            {/* Search */}
            <Col lg={2} md={6}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Search Employee</label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                placeholder="Name, ESI..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>

            <Col lg={2} md={6} className="d-flex gap-2 justify-content-end">
              <Button variant="danger" style={{ height: "38px" }} onClick={exportToPDF}>PDF</Button>
              <Button variant="primary" style={{ height: "38px" }} onClick={() => window.print()}>Print</Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!sessionFirm?.Id ? (
        <Card className="shadow-sm border-0">
          <Card.Body className="text-center p-5">
            <h5 className="text-warning mb-2 fw-semibold">⚠ Session Setup Required</h5>
            <p className="text-muted mb-0 fs-13">
              Please select a **Firm**, **Month**, and **Year** to load the register.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Fragment>
          {/* Web View */}
          <div className="no-print">
            <Card className="shadow-sm border-0" style={{ borderRadius: "10px" }}>
              <Card.Header className="bg-white border-bottom py-3 px-4">
                <div className="text-center">
                  <h4 className="fw-bold mb-1 text-dark fs-15">{sessionFirm?.Name}</h4>
                  <div className="fw-bold text-secondary fs-13">EMPLOYEES STATE INSURANCE REPORT FOR THE MONTH OF {selectedMonth?.toUpperCase()} {selectedYear}</div>
                  <div className="text-muted fs-12">CODE NO. : <strong>{getFirmEsiNo()}</strong></div>
                </div>
              </Card.Header>

              <Card.Body className="p-0">
                {isLoading ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status" />
                    <div className="text-muted mt-2 fs-13">Loading ESI Deduction details...</div>
                  </div>
                ) : records.length === 0 ? (
                  <div className="text-center text-muted p-5">No records found.</div>
                ) : (
                  <div className="table-responsive">
                    <Table bordered hover className="mb-0 text-center" style={{ fontSize: "12px" }}>
                      <thead className="bg-light">
                        <tr>
                          <th style={{ width: "5%" }}>Sr. No.</th>
                          <th>AC No</th>
                          <th>EMPLOYEE NAME / FATHER NAME</th>
                          <th>ELIGIBLE SALARY</th>
                          <th>EMPLOYEE ESI</th>
                          <th>EMPLOYER ESI</th>
                        </tr>
                      </thead>
                      <tbody>
                        {records.map((row) => (
                          <tr key={row.sno}>
                            <td>{row.sno}</td>
                            <td>{row.esiNo}</td>
                            <td className="text-start">
                              <strong>{row.name}</strong><br />
                              <span className="text-muted">{row.fatherName}</span>
                            </td>
                            <td>₹{row.eligibleSalary.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td className="text-danger fw-semibold">₹{row.employeeESI.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td className="text-warning fw-semibold">₹{row.employerESI.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                        <tr className="bg-light fw-bold">
                          <td colSpan={3} className="text-end">Total:</td>
                          <td>₹{records.reduce((sum, r) => sum + r.eligibleSalary, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          <td className="text-danger">₹{records.reduce((sum, r) => sum + r.employeeESI, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          <td className="text-warning">₹{records.reduce((sum, r) => sum + r.employerESI, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* Print View Layout */}
          <div id="print-esi-deduction-container" className="d-none">
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "12px" }}>
              {sessionFirm?.Name?.toUpperCase()}
            </div>
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "10px", margin: "4px 0" }}>
              EMPLOYEES STATE INSURANCE REPORT FOR THE MONTH OF {selectedMonth?.toUpperCase()} {selectedYear}
            </div>
            <div style={{ textAlign: "left", fontSize: "9px", margin: "4px 10px" }}>
              CODE NO. : {getFirmEsiNo()}
            </div>

            <table className="esi-deduction-print-table">
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>AC No</th>
                  <th>EMPLOYEE NAME / FATHER NAME</th>
                  <th>ELIGIBLE SALARY</th>
                  <th>EMPLOYEE ESI</th>
                  <th>EMPLOYER ESI</th>
                </tr>
              </thead>
              <tbody>
                {records.map(row => (
                  <tr key={row.sno}>
                    <td>{row.sno}</td>
                    <td>{row.esiNo}</td>
                    <td>
                      <strong>{row.name}</strong><br />
                      {row.fatherName}
                    </td>
                    <td>{row.eligibleSalary.toFixed(2)}</td>
                    <td>{row.employeeESI.toFixed(2)}</td>
                    <td>{row.employerESI.toFixed(2)}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: "bold", background: "#eee" }}>
                  <td colSpan={3} style={{ textAlign: "right" }}>Total:</td>
                  <td>{records.reduce((sum, r) => sum + r.eligibleSalary, 0).toFixed(2)}</td>
                  <td>{records.reduce((sum, r) => sum + r.employeeESI, 0).toFixed(2)}</td>
                  <td>{records.reduce((sum, r) => sum + r.employerESI, 0).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default EsiDeductionDetail;
